import { forwardRef, memo, useMemo } from 'react';

import { getSpritesheetBackgroundPosition } from '@air/utils-clips';

const MAX_HEIGHT = 120;

export type VideoSeekbarPreviewSpritesheetProps = {
  aspectRatio: number;
  columns: number;
  frameCount: number;
  position: number;
  rows: number;
  src: string;
};

export const VideoSeekbarPreviewSpritesheet = memo(
  forwardRef<HTMLDivElement, VideoSeekbarPreviewSpritesheetProps>(
    ({ aspectRatio, columns, frameCount, position, rows, src }, ref) => {
      const scaledAspectRatio = useMemo(() => {
        const scaledWidth = Math.round(MAX_HEIGHT * aspectRatio);

        return {
          height: MAX_HEIGHT,
          width: scaledWidth,
        };
      }, [aspectRatio]);

      const backgroundSize = useMemo(() => {
        const scaledWidth = scaledAspectRatio.width * columns;
        const scaledHeight = scaledAspectRatio.height * rows;

        return `${scaledWidth}px ${scaledHeight}px`;
      }, [scaledAspectRatio.height, scaledAspectRatio.width, columns, rows]);

      const backgroundPosition = useMemo(() => {
        return getSpritesheetBackgroundPosition({
          columns,
          frameCount,
          frameHeight: scaledAspectRatio.height,
          frameWidth: scaledAspectRatio.width,
          position,
        });
      }, [scaledAspectRatio.height, scaledAspectRatio.width, columns, frameCount, position]);

      return (
        <div
          ref={ref}
          style={{
            backgroundImage: `url(${src})`,
            backgroundPosition,
            backgroundSize,
            ...scaledAspectRatio,
          }}
        />
      );
    },
  ),
);

VideoSeekbarPreviewSpritesheet.displayName = 'VideoSeekbarPreviewSpritesheet';
